import axios from 'axios';

// Use relative URL in production (same domain), or env variable for development
const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Create axios instance
const api = axios.create({
  baseURL: `${API_URL}/api`,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Request interceptor to add auth token
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// ==========================================
// PUBLIC APIs
// ==========================================

export const getCities = () => api.get('/cities');

export const getAmenities = () => api.get('/amenities');

export const searchProperties = (params) => api.get('/search', { params });

export const getPropertyDetail = (propertyId) => api.get(`/properties/${propertyId}`);

export const getPropertyAvailability = (propertyId, from, to) => 
  api.get(`/properties/${propertyId}/availability`, { params: { from, to } });

// ==========================================
// POLICY APIs
// ==========================================

export const getPolicyVersions = () => api.get('/policies/versions');

export const acceptPolicies = (policies) => api.post('/policies/accept', { policies });

export const getAcceptedPolicies = () => api.get('/policies/accepted');

// ==========================================
// AUTH APIs
// ==========================================

export const login = (email, password) => 
  api.post('/auth/login', { email, password });

export const register = (name, email, password, role) => 
  api.post('/auth/register', { name, email, password, role });

export const getMe = () => api.get('/auth/me');

export const updateProfile = (data) => api.put('/auth/profile', data);

export const uploadProfilePhoto = (file) => {
  const formData = new FormData();
  formData.append('file', file);
  return api.post('/auth/profile/photo', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
};

export const deleteProfilePhoto = () => api.delete('/auth/profile/photo');

export const becomeHost = () => api.post('/auth/become-host');

// ==========================================
// GUEST APIs
// ==========================================

export const createBooking = (data) => api.post('/bookings', data);

export const getGuestBookings = (params) => api.get('/bookings', { params });

export const getBookingDetail = (bookingId) => api.get(`/bookings/${bookingId}`);

export const initiatePayment = (bookingId) => api.post(`/bookings/${bookingId}/pay`);

export const getPaymentStatus = (sessionId) => api.get(`/payments/status/${sessionId}`);

// Saved Properties
export const saveProperty = (propertyId) => api.post(`/guest/properties/${propertyId}/save`);

export const unsaveProperty = (propertyId) => api.delete(`/guest/properties/${propertyId}/save`);

export const checkPropertySaved = (propertyId) => api.get(`/guest/properties/${propertyId}/saved`);

export const getSavedProperties = (params) => api.get('/guest/saved-properties', { params });

// ==========================================
// MESSAGING APIs
// ==========================================

export const getBookingMessages = (bookingId, params) => 
  api.get(`/bookings/${bookingId}/messages`, { params });

export const sendMessage = (bookingId, message) => 
  api.post(`/bookings/${bookingId}/messages`, { message_text: message });

// ==========================================
// HOST APIs
// ==========================================

export const getHostProfile = () => api.get('/host/profile');

export const updateHostProfile = (data) => api.put('/host/profile', data);

export const completeHostOnboarding = (policies) => 
  api.post('/host/onboarding/complete', { policies });

export const getHostProperties = (params) => api.get('/host/properties', { params });

export const getHostPropertyDetail = (propertyId) => api.get(`/host/properties/${propertyId}`);

export const createProperty = (data) => api.post('/host/properties', data);

export const updateProperty = (propertyId, data) => api.put(`/host/properties/${propertyId}`, data);

export const uploadPropertyImage = (propertyId, file, isCover = false) => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('is_cover', isCover);
  return api.post(`/host/properties/${propertyId}/images`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
};

export const deletePropertyImage = (propertyId, imageId) => 
  api.delete(`/host/properties/${propertyId}/images/${imageId}`);

export const publishProperty = (propertyId) => 
  api.post(`/host/properties/${propertyId}/publish`);

export const submitPropertyForReview = (propertyId) => 
  api.post(`/host/properties/${propertyId}/submit-for-review`);

export const pauseProperty = (propertyId) => 
  api.post(`/host/properties/${propertyId}/pause`);

export const initStripeConnect = () => api.post('/host/stripe/connect');

export const getStripeConnectStatus = () => api.get('/host/stripe/status');

export const getStripeDashboardLink = () => api.post('/host/stripe/dashboard');

export const continueStripeOnboarding = () => api.post('/host/stripe/continue-onboarding');

// ==========================================
// BOOKING ACTIONS
// ==========================================

export const cancelBooking = (bookingId) => api.post(`/bookings/${bookingId}/cancel`);

export const createAvailabilityBlock = (propertyId, data) => 
  api.post(`/host/properties/${propertyId}/availability/block`, data);

export const deleteAvailabilityBlock = (propertyId, blockId) => 
  api.delete(`/host/properties/${propertyId}/availability/block/${blockId}`);

export const getPropertyCalendar = (propertyId, month, year) =>
  api.get(`/host/properties/${propertyId}/calendar`, { params: { month, year } });

export const getHostBookings = (params) => api.get('/host/bookings', { params });

export const approveBooking = (bookingId) => api.post(`/host/bookings/${bookingId}/approve`);

export const declineBooking = (bookingId, message) => 
  api.post(`/host/bookings/${bookingId}/decline`, { message });

export const getHostPayouts = (params) => api.get('/host/payouts', { params });

// ==========================================
// REVIEW APIs
// ==========================================

export const createReview = (data) => api.post('/bookings/reviews', data);

export const getGuestReviews = (params) => api.get('/bookings/reviews', { params });

export const getPropertyReviews = (propertyId, params) => 
  api.get(`/properties/${propertyId}/reviews`, { params });

export const getHostReviews = (params) => api.get('/host/reviews', { params });

export const replyToReview = (reviewId, replyText) => 
  api.post(`/host/reviews/${reviewId}/reply`, { reply_text: replyText });

export const toggleReviewVisibility = (reviewId) => 
  api.put(`/admin/reviews/${reviewId}/visibility`);

export const getAdminReviews = (params) => api.get('/admin/reviews', { params });

// ==========================================
// ADMIN APIs
// ==========================================

export const getAdminUsers = (params) => api.get('/admin/users', { params });

export const getAdminProperties = (params) => api.get('/admin/properties', { params });

export const getAdminBookings = (params) => api.get('/admin/bookings', { params });

export const suspendProperty = (propertyId) => api.post(`/admin/properties/${propertyId}/suspend`);

export const suspendUser = (userId) => api.post(`/admin/users/${userId}/suspend`);

export const refundBooking = (bookingId) => api.post(`/admin/bookings/${bookingId}/refund`);

export const getAdminMetrics = () => api.get('/admin/metrics');

export const getAdminPropertyStatusCounts = () => api.get('/admin/property-status-counts');

// P4 Admin Moderation APIs
export const getAdminHosts = (params) => api.get('/admin/hosts', { params });

export const approveHost = (userId) => api.post(`/admin/hosts/${userId}/approve`);

export const declineHost = (userId, reason) => api.post(`/admin/hosts/${userId}/decline`, { reason });

export const deleteProperty = (propertyId) => api.post(`/admin/properties/${propertyId}/delete`);

export const approveProperty = (propertyId) => api.post(`/admin/properties/${propertyId}/approve`);

export const rejectProperty = (propertyId, reason) => 
  api.post(`/admin/properties/${propertyId}/reject`, { reason });

export const migratePublishedToApproved = () => api.post('/admin/migrate-published-to-approved');

export const getAdminPropertyDetail = (propertyId) => api.get(`/admin/properties/${propertyId}/detail`);

export const getAdminAuditLog = (params) => api.get('/admin/audit-log', { params });

export default api;
